import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { isAfter, isBefore } from 'date-fns';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { createContext } from 'react';

import { getAuctionApi, postAuctionBidApi } from '@flipster/apis/v1/auction';
import { AuctionBidder, AuctionItem } from '@flipster/apis/v1/auction/types';
import { getUserPointApi } from '@flipster/apis/v1/user';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { timestampToDate } from '@flipster/utils/datetime';

// TODO: 위치 옮기고 기존 useUserPointApi를 사용하는 곳에서도 spark bunja를 사용하도록 수정하기
export const sparkBunja = bunja(() => {
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const getUserPointApiAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: ['getUserPointApi', isCookieLoggedIn],
      queryFn: getUserPointApi,
      enabled: isCookieLoggedIn,
    };
  });

  const mySparksAtom = atom((get) => {
    const { data } = get(getUserPointApiAtom);
    return Number(data?.data.points || 0);
  });

  return { getUserPointApiAtom, mySparksAtom };
});
sparkBunja.debugLabel = 'sparkBunja';

export const SparkAuctionIdContext = createContext<string>('');
export const SparkAuctionIdScope = createScopeFromContext(SparkAuctionIdContext);

export const sparkAuctionsBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { getUserPointApiAtom } = bunja.use(sparkBunja);
  const getAuctionApiAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    return {
      queryKey: ['getAuctionApi', isLoggedIn],
      queryFn: getAuctionApi,
    };
  });

  const isLoadingAtom = atom((get) => {
    return get(getAuctionApiAtom).isLoading;
  });

  async function bidAuction(id: string, amount: number) {
    await postAuctionBidApi(id, { amount });
    await Promise.all([store.get(getAuctionApiAtom).refetch(), store.get(getUserPointApiAtom).refetch()]);
  }

  const nowAtom = atom(new Date());

  const auctionListAtom = atom((get) => {
    const { data } = get(getAuctionApiAtom);
    return data?.data.list || [];
  });

  const auctionRecordAtom = atom((get) => {
    const list = get(auctionListAtom);
    const result: Record<string, AuctionItem> = {};
    for (const auction of list) result[auction.id] = auction;
    return result;
  });

  const currentAuctionAtom = atom((get) => {
    const now = get(nowAtom);
    const list = get(auctionListAtom);
    return list.find((auction) => {
      if (isAfter(now, timestampToDate(auction.displayEndTimestamp))) return false;
      if (isBefore(now, timestampToDate(auction.displayStartTimestamp))) return false;
      return true;
    });
  });

  const currentAuctionIdAtom = atom((get) => {
    const currentAuction = get(currentAuctionAtom);
    return currentAuction?.id;
  });

  const pastAuctionsAtom = atom((get) => {
    const now = get(nowAtom);
    const list = get(auctionListAtom);
    return list
      .filter((auction) => isAfter(now, timestampToDate(auction.displayEndTimestamp)))
      .filter((auction) => auction.topBidders.length > 0)
      .sort((a, b) => {
        const aa = timestampToDate(a.startTimestamp);
        const bb = timestampToDate(b.startTimestamp);
        return isAfter(aa, bb) ? -1 : isBefore(aa, bb) ? 1 : 0;
      })
      .slice(0, 5);
  });

  const pastAuctionIdsAtom = atom((get) => {
    const pastAuctions = get(pastAuctionsAtom);
    return pastAuctions.map((auction) => auction.id);
  });

  bunja.effect(() => {
    const refreshInterval = setInterval(() => store.get(getAuctionApiAtom).refetch(), 10000);
    const countdownInterval = setInterval(() => store.set(nowAtom, new Date()), 1000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  });

  return {
    bidAuction,
    nowAtom,
    isLoadingAtom,
    auctionListAtom,
    auctionRecordAtom,
    currentAuctionIdAtom,
    pastAuctionIdsAtom,
  };
});
sparkAuctionsBunja.debugLabel = 'sparkAuctionsBunja';

export const sparkAuctionBunja = bunja(() => {
  const auctionId = bunja.use(SparkAuctionIdScope);
  const { auctionRecordAtom, nowAtom } = bunja.use(sparkAuctionsBunja);
  const { mySparksAtom } = bunja.use(sparkBunja);
  const { userAtom } = bunja.use(authBunja);
  const sparkAuctionAtom = atom((get) => {
    const auctionRecord = get(auctionRecordAtom);
    const auction = auctionRecord[auctionId];
    if (!auction) throw new Error(`Auction ${auctionId} not found`);
    return auction;
  });

  const auctionIsClosedAtom = atom((get) => {
    const auction = get(sparkAuctionAtom);
    return auction.auctionStatus === 'CLOSED';
  });

  const auctionDatesAtom = atom<AuctionDates>((get) => {
    const auction = get(sparkAuctionAtom);
    return {
      displayStart: timestampToDate(auction.startTimestamp),
      start: timestampToDate(auction.startTimestamp),
      rewardReveal: timestampToDate(auction.rewardRevealTimestamp),
      end: timestampToDate(auction.endTimestamp),
      displayEnd: timestampToDate(auction.endTimestamp),
    };
  });

  const prizeRevealDDayAtom = atom((get) => {
    const now = get(nowAtom);
    const { rewardReveal } = get(auctionDatesAtom);
    const diff = Number(rewardReveal) - Number(now);
    return Math.floor(diff / millisecondsInDay);
  });

  const prizeRevealMessageAtom = atom((get) => {
    const { reward } = get(sparkAuctionAtom);
    const prizeRevealDDay = get(prizeRevealDDayAtom);
    if (prizeRevealDDay > 0) return `Prize reveal D-${prizeRevealDDay}`;
    if (prizeRevealDDay === 0) return 'Prize will reveal in a few hours';
    return `The highest bid wins ${reward}`;
  });

  const topNBiddersAtom = atom<(TopNBidderItem | undefined)[]>((get) => {
    const auction = get(sparkAuctionAtom);
    const sortedBidders = auction.topBidders.sort((a, b) => b.amount - a.amount);
    const frame: (TopNBidderItem | undefined)[] = Array(auction.topBiddersNum).fill(undefined);
    const len = Math.min(sortedBidders.length, frame.length);
    for (let i = 0; i < len; ++i) {
      const bidder = sortedBidders[i];
      frame[i] = { ...bidder, isMe: i === auction.myRanking - 1 };
    }
    return frame;
  });

  const highestBidderAtom = atom<TopNBidderItem | undefined>((get) => {
    const topNBidders = get(topNBiddersAtom);
    return topNBidders[0];
  });

  const myNicknameAtom = atom((get) => {
    const user = get(userAtom);
    return user?.nickname || '';
  });

  const myBidAmountAtom = atom((get) => {
    const auction = get(sparkAuctionAtom);
    return auction.myBidAmount;
  });

  const isFirstBidAtom = atom((get) => get(myBidAmountAtom) === 0);

  const bidInfoAtom = atom((get) => {
    const nickname = get(myNicknameAtom);
    const amount = get(myBidAmountAtom);
    const highestBidder = get(highestBidderAtom);
    const me = { nickname, amount };
    const myAvailableTotalSparks = get(mySparksAtom);
    const isFirstBid = get(isFirstBidAtom);
    return { highestBidder, me, myAvailableTotalSparks, isFirstBid };
  });

  return {
    auctionId,
    sparkAuctionAtom,
    auctionDatesAtom,
    auctionIsClosedAtom,
    prizeRevealMessageAtom,
    topNBiddersAtom,
    highestBidderAtom,
    myBidAmountAtom,
    isFirstBidAtom,
    bidInfoAtom,
  };
});
sparkAuctionBunja.debugLabel = 'sparkAuctionBunja';

export interface AuctionDates {
  displayStart: Date;
  start: Date;
  rewardReveal: Date;
  end: Date;
  displayEnd: Date;
}

export interface TopNBidderItem extends AuctionBidder {
  isMe: boolean;
}

const millisecondsInDay = 86400000;
